// Campaign links carry ?source=<code>. Every code gets a row here before its
// first send.
// The g892308-N codes from the September 2026 positioning test are opaque and
// decode only through this registry; new codes are readable
// (<campaign>-<angle>-<group>, e.g. "positioning-w4-bundle-g4") so the raw
// source is meaningful everywhere it lands, and this registry only confirms
// the code is real and picks its welcome copy.
export type CampaignClaimKey =
  | "adToPage"
  | "agentSetup"
  | "tenPages"
  | "conversionAudit"
  | "quizFunnel"
  | "bundleBuilder";

export type CampaignCodeEntry = {
  // Stable campaign id, e.g. "positioning-test-w3". Matches the HubSpot utm_campaign family.
  campaign: string;
  // The pitch the email made, e.g. "bundle-builder".
  angle: string;
  // The fixed audience group that received it, e.g. "g4".
  group: string;
  // Which welcome-screen claim to show, keyed into campaignContent's claims.
  claim: CampaignClaimKey;
};

const CAMPAIGN_CODES = {
  "g892308-1": {
    campaign: "positioning-test-w1",
    angle: "ad-to-landing-page",
    group: "g1",
    claim: "adToPage",
  },
  "g892308-2": {
    campaign: "positioning-test-w1",
    angle: "agent-setup",
    group: "g2",
    claim: "agentSetup",
  },
  "g892308-3": {
    campaign: "positioning-test-w1",
    angle: "landing-page-speed",
    group: "g3",
    claim: "tenPages",
  },
  "g892308-4": {
    campaign: "positioning-test-w1",
    angle: "leak-finder",
    group: "g4",
    claim: "conversionAudit",
  },
  "g892308-6": {
    campaign: "positioning-test-w2",
    angle: "ad-to-landing-page",
    group: "g1",
    claim: "adToPage",
  },
  "g892308-7": {
    campaign: "positioning-test-w2",
    angle: "ad-to-landing-page",
    group: "g2",
    claim: "adToPage",
  },
  "g892308-8": {
    campaign: "positioning-test-w2",
    angle: "leak-finder",
    group: "g3",
    claim: "conversionAudit",
  },
  "g892308-9": {
    campaign: "positioning-test-w2",
    angle: "leak-finder",
    group: "g4",
    claim: "conversionAudit",
  },
  "g892308-11": {
    campaign: "positioning-test-w3",
    angle: "quiz-funnel",
    group: "g1",
    claim: "quizFunnel",
  },
  "g892308-12": {
    campaign: "positioning-test-w3",
    angle: "bundle-builder",
    group: "g2",
    claim: "bundleBuilder",
  },
  "g892308-13": {
    campaign: "positioning-test-w3",
    angle: "quiz-funnel",
    group: "g3",
    claim: "quizFunnel",
  },
  "g892308-14": {
    campaign: "positioning-test-w3",
    angle: "bundle-builder",
    group: "g4",
    claim: "bundleBuilder",
  },
} satisfies Record<string, CampaignCodeEntry>;

export const campaignCodes: readonly string[] = Object.keys(CAMPAIGN_CODES);

// Own-property guard: `source` is user-controlled, and a bare Record lookup
// would return inherited members for keys like "toString".
export function getCampaignCodeEntry(
  source: string | null,
): CampaignCodeEntry | null {
  if (!source || !Object.hasOwn(CAMPAIGN_CODES, source)) {
    return null;
  }
  const entries: Record<string, CampaignCodeEntry> = CAMPAIGN_CODES;
  return entries[source] ?? null;
}

export type CampaignEventProperties = {
  source: string | null;
  campaign: string | null;
  campaignAngle: string | null;
  campaignGroup: string | null;
};

// Analytics properties for the campaign funnel events, so PostHog shows
// "positioning-test-w3 / bundle-builder" next to the raw code. Unknown or
// absent sources keep `source` as-is and leave the decoded fields null.
export function getCampaignEventProperties({
  source,
}: {
  source: string | null;
}): CampaignEventProperties {
  const entry = getCampaignCodeEntry(source);
  return {
    source,
    campaign: entry?.campaign ?? null,
    campaignAngle: entry?.angle ?? null,
    campaignGroup: entry?.group ?? null,
  };
}
